import React, { useEffect, useState } from 'react';
import useAxios from 'axios-hooks';
import { PrimaryButton, Checkbox } from '@fluentui/react';
import { Field, Form, Formik } from 'formik';
import { useParams, useHistory } from 'react-router-dom';
import TextField from '../../../../Forms/TextField';
import Toast from '../../../../Forms/Toast';
import FeatureSummary from '../../../../Util/FeatureSummary';
import Select from "react-select";
import { FixedSizeList as List } from "react-window";

const newSchoolObject = {
    clientID: null,
    schoolName: '',
    coBrandID: null,
    displayOnSignup: false,
    userFacingName: '',
};

const CustomMenuList = ({ children, ...props }) => {
    const height = 35;
    const itemCount = children.length;
    const totalHeight = Math.min(300, itemCount * height);

    return (
        <List height={totalHeight} itemCount={itemCount} itemSize={height} width="100%">
            {({ index, style }) => (
                <div
                    style={{
                        ...style,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        padding: "0 8px",
                        boxSizing: "border-box",
                    }}
                    title={children[index]?.props?.children}
                >
                    {children[index]}
                </div>
            )}
        </List>
    );
};

const WizardPage = () => {
    const [showToast, setShowToast] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const history = useHistory();
    const { clientSchoolId: idFromParams } = useParams();

    const [{ data: schoolData }, getSchool] = useAxios(
        { url: `/clients/schools/${idFromParams}`, method: 'GET' },
        { manual: true }
    );

    const [{ data: cobrandsData }] = useAxios('/internal/cobrands?bustCache=true');
    const [{ data: clients }] = useAxios('/internal/clients?bustCache=true');

    const clientsOptions = clients?.map(u => ({ value: u.clientID, label: u.name })) || [];

    useEffect(() => {
        if (idFromParams) {
            getSchool();
        }
    }, [idFromParams]);

    const [{ loading: submitting }, submitForm] = useAxios(
        { url: `/clients/schools` },
        { manual: true }
    );

    // Transform school data for form
    const initialValues = schoolData ? {
        clientSchoolID: schoolData.clientSchoolID,
        clientID: schoolData.clientID,
        schoolName: schoolData.schoolName,
        coBrandID: schoolData.coBrandID,
        displayOnSignup: schoolData.displayOnSignup || false,
        userFacingName: schoolData.userFacingName || '',
    } : newSchoolObject;

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={async (values, { resetForm }) => {
                setShowError(false);
                setErrorMessage('');

                // Validation: if displayOnSignup is true, userFacingName is required
                if (values.displayOnSignup && !values.userFacingName?.trim()) {
                    setShowError(true);
                    setErrorMessage('User Facing Name is required when Display On Signup is checked');
                    return;
                }

                try {
                    const result = await submitForm({
                        url: idFromParams ? "/clients/schools" : "/clients/schools",
                        method: idFromParams ? "PUT" : "POST",
                        data: values,
                    });

                    if (!result.isAxiosError) {
                        if (!idFromParams) {
                            // Navigate to edit mode after creation
                            history.push(`/client-settings-feature/cobrand-units/${result.data}/wizard`);
                        }
                        setShowToast(true);
                    } else {
                        setShowError(true);
                        setErrorMessage('Failed to save school');
                    }
                } catch (error) {
                    setShowError(true);
                    setErrorMessage(error.message || 'An error occurred');
                }
            }}
            enableReinitialize
            isInitialValid={false}
        >
            {({ errors, values, setFieldValue, handleSubmit, dirty }) => {
                const isButtonDisabled = !dirty || 
                    !values.clientID || 
                    !values.schoolName?.trim() || 
                    !values.coBrandID ||
                    (values.displayOnSignup && !values.userFacingName?.trim());

                // Filter cobrands by selected client
                const filteredCobrands = cobrandsData?.filter(c => c.clientID === values.clientID) || [];
                const cobrandsOptions = filteredCobrands.map(c => ({ value: c.cobrandId, label: c.name }));

                return (
                    <Form style={{ maxWidth: "600px", marginBottom: 500 }}>
                        <div className="displayflex mb20" style={{ justifyContent: 'space-between' }}>
                            <h4>{idFromParams ? 'Edit Cobrand Unit' : 'Create Cobrand Unit'}</h4>
                            <PrimaryButton
                                onClick={handleSubmit}
                                text={idFromParams ? "Update" : 'Create'}
                                disabled={isButtonDisabled}
                            />
                        </div>

                        {showError && (
                            <div style={{ color: 'rgb(164, 38, 44)', marginBottom: 10 }}>
                                {errorMessage || 'An error has occurred.'}
                            </div>
                        )}

                        <div className='panel-h' style={{fontWeight:'500', marginBottom:5}}>Client</div>
                        <Select
                            components={{ MenuList: CustomMenuList }}
                            options={clientsOptions}
                            placeholder="Select Client"
                            name="clientName"
                            value={clientsOptions.find(option => option.value === values.clientID)}
                            onChange={(option) => {
                                setFieldValue('clientID', option.value);
                                // Reset cobrand when client changes
                                setFieldValue('coBrandID', null);
                            }}
                            isSearchable
                            styles={{
                                container: base => ({ ...base, width: '100%', marginBottom: 10 }),
                                control: base => ({ ...base, border: "1px solid #aaaaaa" }),
                                menuList: base => ({ ...base, maxHeight: "300px", overflowY: "auto" }),
                            }}
                        />

                        <div className='panel-h' style={{fontWeight:'500', marginBottom:5, marginTop:15}}>Cobrand</div>
                        <Select
                            components={{ MenuList: CustomMenuList }}
                            options={cobrandsOptions}
                            placeholder="Select Cobrand"
                            name="coBrandName"
                            value={cobrandsOptions.find(option => option.value === values.coBrandID)}
                            onChange={(option) => {
                                setFieldValue('coBrandID', option.value);
                            }}
                            isSearchable
                            isDisabled={!values.clientID}
                            styles={{
                                container: base => ({ ...base, width: '100%', marginBottom: 10 }),
                                control: base => ({ ...base, border: "1px solid #aaaaaa" }),
                                menuList: base => ({ ...base, maxHeight: "300px", overflowY: "auto" }),
                            }}
                        />

                        <TextField
                            label="Unit Name"
                            className="mt10"
                            name="schoolName"
                            value={values.schoolName}
                            onChange={(e, v) => setFieldValue('schoolName', v?.trimStart())}
                            errorMessage={errors.schoolName}
                            required
                        />

                        <Checkbox
                            label="Display On Signup"
                            checked={values.displayOnSignup}
                            onChange={(e, checked) => {
                                setFieldValue('displayOnSignup', checked);
                                // Clear user facing name when unchecked
                                if (!checked) {
                                    setFieldValue('userFacingName', '');
                                }
                            }}
                            styles={{ root: { marginTop: 15 } }}
                        />

                        {values.displayOnSignup && (
                            <TextField
                                label="User Facing Name"
                                className="mt10"
                                name="userFacingName"
                                value={values.userFacingName}
                                onChange={(e, v) => setFieldValue('userFacingName', v)}
                                errorMessage={errors.userFacingName}
                                required
                                description="This name will be displayed to users during signup"
                            />
                        )}

                        <Toast show={showToast} message={idFromParams ? "Updated!" : "Created!"} onClose={() => setShowToast(false)} />
                        <FeatureSummary
                            label="About Cobrand Units"
                            summary="Configure school/unit records within a client organization. Set the unit name, associate with a cobrand, and control whether it appears in the signup flow. When displayed on signup, specify the user-facing name shown to users during registration."
                        />
                    </Form>
                );
            }}
        </Formik>
    );
};

export default WizardPage;
